// Localized error messages for API errors (single source of truth used by
// errMsg in lib/api.js). errMsg runs outside React, so language is read from
// localStorage['lang'] here rather than from the i18n context.

const getLang = () => {
  try {
    const l = localStorage.getItem('lang');
    return l === 'en' ? 'en' : 'de';
  } catch {
    return 'de';
  }
};

// Machine error code -> localized message (params interpolated). DE + EN parity.
const CODE_MESSAGES = {
  INVALID_CREDENTIALS: { de: 'Ungültige Anmeldedaten.', en: 'Invalid email or password.' },
  ACCOUNT_DISABLED: { de: 'Dein Konto ist deaktiviert. Bitte kontaktiere deinen Coach.', en: 'Your account is disabled. Please contact your coach.' },
  USER_ALREADY_EXISTS: { de: 'Es existiert bereits ein Konto mit diesen Daten.', en: 'An account with these details already exists.' },
  EMAIL_OR_PHONE_REQUIRED: { de: 'E-Mail oder Telefonnummer erforderlich.', en: 'Email or phone number is required.' },
  CURRENT_PASSWORD_INCORRECT: { de: 'Aktuelles Passwort ist nicht korrekt.', en: 'Your current password is incorrect.' },
  PASSWORD_UNCHANGED: { de: 'Neues Passwort muss sich vom aktuellen unterscheiden.', en: 'New password must be different from the current one.' },
  RESET_TOKEN_INVALID: { de: 'Der Link ist ungültig oder abgelaufen.', en: 'This link is invalid or has expired.' },
  PACKAGE_REQUIRED: { de: 'Kein Paket aktiv. Bitte kontaktiere deinen Coach.', en: 'No active package. Please contact your coach.' },
  ENTITLEMENT_REQUIRED: { de: 'Dieser Bereich ist in deinem Paket nicht enthalten.', en: 'This area is not included in your package.' },
  INVALID_WEEK: { de: 'Ungültige Wochennummer.', en: 'Invalid week number.' },
  UNAUTHORIZED: { de: 'Bitte melde dich an.', en: 'Please sign in.' },
  FORBIDDEN: { de: 'Kein Zugriff.', en: 'Access denied.' },
  NOT_FOUND: { de: 'Nicht gefunden.', en: 'Not found.' },
  SERVER_ERROR: { de: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.', en: 'Something went wrong. Please try again.' },
};

const withParams = (code, params) => {
  const p = params || {};
  const lang = getLang();
  switch (code) {
    case 'RATE_LIMITED':
      if (p.minutes)
        return lang === 'en'
          ? `Too many attempts. Please try again in ${p.minutes} minute(s).`
          : `Zu viele Versuche. Bitte in ${p.minutes} Minute(n) erneut versuchen.`;
      return lang === 'en'
        ? 'Too many attempts. Please try again later.'
        : 'Zu viele Versuche. Bitte später erneut versuchen.';
    case 'INVALID_PASSWORD_POLICY':
      if (p.reason === 'too_long')
        return lang === 'en' ? 'Password is too long (max 72 bytes).' : 'Passwort ist zu lang (maximal 72 Byte).';
      return lang === 'en'
        ? `Password must be at least ${p.min || 10} characters.`
        : `Passwort muss mindestens ${p.min || 10} Zeichen haben.`;
    case 'IMAGE_TOO_LARGE':
      return lang === 'en'
        ? 'Image is too large. Please use a smaller photo.'
        : 'Bild zu groß. Bitte ein kleineres Foto verwenden.';
    case 'UNSUPPORTED_IMAGE_TYPE':
      return lang === 'en'
        ? `Unsupported image type${p.mime ? ` (${p.mime})` : ''}.`
        : `Nicht unterstützter Bildtyp${p.mime ? ` (${p.mime})` : ''}.`;
    case 'ACTIVE_WEEK_ONLY':
      return lang === 'en'
        ? `You can only confirm the active week (week ${p.week ?? ''}).`.replace(' )', ')')
        : `Du kannst nur die aktive Woche bestätigen (Woche ${p.week ?? ''}).`.replace(' )', ')');
    default:
      return null;
  }
};

// Known legacy German backend `detail` strings -> {de, en}. Safety net so an
// English client never sees a German string from an endpoint that still returns
// a plain string detail (e.g. coach-only endpoints not migrated to codes).
const LEGACY_DE = {
  'Ungültige Anmeldedaten': { en: 'Invalid email or password.' },
  'Konto deaktiviert': { en: 'Your account is disabled. Please contact your coach.' },
  'Benutzer existiert bereits': { en: 'An account with these details already exists.' },
  'Email oder Telefonnummer erforderlich': { en: 'Email or phone number is required.' },
  'Aktuelles Passwort ist nicht korrekt.': { en: 'Your current password is incorrect.' },
  'Neues Passwort muss sich vom aktuellen unterscheiden.': { en: 'New password must be different from the current one.' },
  'Der Link ist ungültig oder abgelaufen.': { en: 'This link is invalid or has expired.' },
  'Passwort ist zu lang (maximal 72 Byte).': { en: 'Password is too long (max 72 bytes).' },
  'Ungültige Wochennummer': { en: 'Invalid week number.' },
  'Zu viele Registrierungsversuche. Bitte später erneut versuchen.': { en: 'Too many registration attempts. Please try again later.' },
  'Zu viele Versuche. Bitte in einigen Minuten erneut versuchen.': { en: 'Too many attempts. Please try again in a few minutes.' },
  'Kunde nicht gefunden': { en: 'Client not found.' },
  'Plan nicht gefunden': { en: 'Plan not found.' },
  'Kein Template für Fallback verfügbar': { en: 'No template available for fallback.' },
  'Antworttext darf nicht leer sein.': { en: 'The reply text cannot be empty.' },
  'Check-in nicht gefunden.': { en: 'Check-in not found.' },
  'Foto nicht gefunden.': { en: 'Photo not found.' },
  'Unbekanntes Paket': { en: 'Unknown package.' },
};

// Match dynamic German strings that carry a number (e.g. lockout / week / policy).
const legacyDynamic = (s, lang) => {
  if (lang !== 'en') return null;
  let m;
  if ((m = s.match(/^Zu viele Anmeldeversuche\. Bitte in (\d+) Minute\(n\) erneut versuchen\.$/)))
    return `Too many login attempts. Please try again in ${m[1]} minute(s).`;
  if ((m = s.match(/^Passwort muss mindestens (\d+) Zeichen haben\.$/)))
    return `Password must be at least ${m[1]} characters.`;
  if ((m = s.match(/^Du kannst nur die aktive Woche bestätigen \(Woche (\d+)\)\.$/)))
    return `You can only confirm the active week (week ${m[1]}).`;
  if ((m = s.match(/^Du kannst nur die aktive Woche bearbeiten \(Woche (\d+)\)\.$/)))
    return `You can only edit the active week (week ${m[1]}).`;
  if ((m = s.match(/^Woche (\d+) ist abgeschlossen und kann nicht mehr bearbeitet werden\.$/)))
    return `Week ${m[1]} is completed and can no longer be edited.`;
  if ((m = s.match(/^Nicht unterstützter Bildtyp \(([^)]*)\): (.+)$/)))
    return `Unsupported image type (${m[1]}): ${m[2]}`;
  if ((m = s.match(/^Bild zu groß \(([^)]*)\)\./)))
    return `Image (${m[1]}) is too large. Please use a smaller photo.`;
  return null;
};

// Resolve a code-based error detail to a localized string.
export const errorMessage = (code, params) => {
  const lang = getLang();
  const dyn = withParams(code, params);
  if (dyn) return dyn;
  const m = CODE_MESSAGES[code];
  if (m) return m[lang] || m.de;
  return CODE_MESSAGES.SERVER_ERROR[lang];
};

// Resolve a legacy plain-string German detail to the current language.
export const legacyMessage = (s) => {
  const lang = getLang();
  if (lang !== 'en') return s; // German user: the original German string is correct
  const dyn = legacyDynamic(s, lang);
  if (dyn) return dyn;
  const hit = LEGACY_DE[s];
  return hit ? hit.en : s;
};

export const genericError = () => CODE_MESSAGES.SERVER_ERROR[getLang()];

const DYNAMIC_CODES = ['RATE_LIMITED', 'INVALID_PASSWORD_POLICY', 'IMAGE_TOO_LARGE', 'UNSUPPORTED_IMAGE_TYPE', 'ACTIVE_WEEK_ONLY'];
export const isKnownCode = (code) => !!CODE_MESSAGES[code] || DYNAMIC_CODES.includes(code);
